function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, useRef, Children, useState, useEffect } from "react";
import EdenContextualMenu from "@churchofjesuschrist/eden-contextual-menu";
import PropTypes from "prop-types";
import styled from "styled-components";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
import sharedCss, { internalUseOnlyOpenDetailsStyles } from "./sharedCss";
import { spacing8, spacing4 } from "@churchofjesuschrist/eden-style-constants";
import { ValidityStateContext } from "./validityState";
import { Label, Checkbox, FieldSet as EdenFieldSet } from "../index";
const useCombinedRefs = function () {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  const targetRef = useRef();
  React.useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);
  return targetRef;
};
export const InternalUseOnlySelect = styled.select.withConfig({
  componentId: "sc-11vq36o-0"
})(["", ";margin:0;appearance:none;padding:", ";", ";&[data-loading]{opacity:0;}", ";"], sharedCss, props => props.small ? `${spacing4} ${spacing8}` : spacing8, internalUseOnlyOpenDetailsStyles({
  Icon: ArrowDropDown,
  small: true
}), _ref => {
  let {
    hasOverflow,
    multiple
  } = _ref;
  return hasOverflow && multiple ? `
    overflow: hidden;
    & > option {
      pointer-events: none;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  
    & > option:not(:first-child) {
      display: none;
      opacity: 0;
    }
    ` : multiple ? `
    min-width: 14ch;
    ` : "";
});
const ContextualMenu = styled(EdenContextualMenu).withConfig({
  componentId: "sc-11vq36o-1"
})(["max-height:275px;min-width:200px;max-width:100%;"]);
const FieldSet = styled(EdenFieldSet).withConfig({
  componentId: "sc-11vq36o-2"
})(["& > div{width:100%;}"]);

/**
 * The Select component allows users to choose a single option from a list of options that appears when the form is activated.
 */
const Select = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    validityState,
    small,
    children,
    name,
    required,
    value,
    defaultValue,
    onChange = x => x,
    multiple,
    ...props
  } = _ref2;
  const initialValue = value || defaultValue || [];
  const [internalValue, setInternalValue] = useState(initialValue);
  const stringifyValue = JSON.stringify(initialValue);
  const [stringifyInternalValue, setStringifyInternalValue] = useState(stringifyValue);
  const prevStringifyInternalValue = useRef(stringifyInternalValue);
  const childrenCount = React.Children.count(children);
  const validityStateContext = useContext(ValidityStateContext);
  const overflowRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, overflowRef);
  const [open, setOpen] = useState(false);
  const fieldSetRef = useRef(null);
  const [activeChild, setActiveChild] = useState(1);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (overflowRef.current && (!multiple || childrenCount > 1)) {
      var _overflowRef$current, _overflowRef$current2;
      setHasOverflow(((_overflowRef$current = overflowRef.current) === null || _overflowRef$current === void 0 ? void 0 : _overflowRef$current.scrollHeight) > ((_overflowRef$current2 = overflowRef.current) === null || _overflowRef$current2 === void 0 ? void 0 : _overflowRef$current2.offsetHeight));
      setIsLoading(false);
    }
  }, [overflowRef, multiple, childrenCount]);
  useEffect(() => {
    if (open === true) {
      const fieldSet = fieldSetRef.current;
      if (!fieldSet) {
        return;
      }
      const firstChecked = fieldSet.querySelector(`input:checked`);
      const animationFrame = requestAnimationFrame(() => {
        if (firstChecked) {
          firstChecked.focus();
          setActiveChild(Array.from(firstChecked.parentNode.parentNode.children).indexOf(firstChecked.parentNode) + 1);
        } else {
          fieldSet.querySelector(`label:nth-child(1)`).focus();
          setActiveChild(1);
        }
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [open]);
  useEffect(() => {
    setInternalValue(JSON.parse(stringifyValue));
  }, [stringifyValue]);
  useEffect(() => {
    if (prevStringifyInternalValue.current !== stringifyInternalValue) {
      var _overflowRef$current3;
      prevStringifyInternalValue.current = stringifyInternalValue;
      overflowRef === null || overflowRef === void 0 ? void 0 : (_overflowRef$current3 = overflowRef.current) === null || _overflowRef$current3 === void 0 ? void 0 : _overflowRef$current3.dispatchEvent(new Event("change", {
        bubbles: true
      }));
    }
  }, [stringifyInternalValue]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InternalUseOnlySelect, _extends({
    hasOverflow: hasOverflow,
    validityState: validityState ? validityState : validityStateContext,
    small: small,
    name: name,
    required: required,
    value: hasOverflow ? internalValue : value,
    defaultValue: defaultValue,
    onChange: onChange,
    ref: combinedRef,
    multiple: multiple
  }, isLoading ? {
    "data-loading": true
  } : {}, hasOverflow ? {
    onMouseDown: event => {
      event.preventDefault();
    },
    onClick: event => {
      event.preventDefault();
      setOpen(true);
    },
    onKeyDown: event => {
      if (event.code === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
      }
      if (event.code === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
      }
      if (event.code === "Space") {
        event.preventDefault();
        setOpen(true);
      }
    },
    onScroll: event => {
      event.preventDefault();
      event.target.scrollTop = 0;
    }
  } : {}, props), hasOverflow ? /*#__PURE__*/React.createElement("option", {
    value: "eden-multiple-select-display-value"
  }, React.Children.toArray(children).filter(child => {
    if (typeof child.props.value !== "undefined") {
      return internalValue.includes(child.props.value);
    } else {
      return internalValue.includes(child.props.children);
    }
  }).map(child => child.props.children || child.props.value).join(", ")) : null, children), hasOverflow && overflowRef.current ? /*#__PURE__*/React.createElement(ContextualMenu, {
    forRef: overflowRef,
    style: {
      width: overflowRef.current.offsetWidth + "px"
    },
    arrow: "none",
    open: open,
    onClickOutside: () => {
      setOpen(false);
    },
    onKeyDown: event => {
      const fieldSet = fieldSetRef.current;
      if (event.code === "ArrowDown") {
        event.preventDefault();
        if (activeChild < fieldSet.querySelectorAll("label").length) {
          const newActiveChild = activeChild + 1;
          fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
          setActiveChild(newActiveChild);
        }
      }
      if (event.code === "ArrowUp") {
        event.preventDefault();
        if (activeChild > 1) {
          const newActiveChild = activeChild - 1;
          fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
          setActiveChild(newActiveChild);
        }
      }
      if (event.code === "Escape") {
        event.preventDefault();
        setOpen(false);
        overflowRef.current.focus();
      }
      if (event.code === "Enter") {
        event.preventDefault();
        fieldSet.querySelector(`label:nth-child(${activeChild})`).click();
      }
      if (event.code === "Tab") {
        setOpen(false);
      }
    }
  }, /*#__PURE__*/React.createElement(FieldSet, {
    ref: fieldSetRef
  }, Children.toArray(children).map((_ref3, index) => {
    let {
      props
    } = _ref3;
    const childValue = props.value || props.children;
    if (props.disabled) {
      return;
    }
    return /*#__PURE__*/React.createElement(Label, {
      key: childValue
    }, /*#__PURE__*/React.createElement(Checkbox, {
      tabIndex: "-1",
      checked: internalValue.includes(childValue),
      onChange: event => {
        setActiveChild(index + 1);
        if (event.target.checked) {
          const updatedValue = [...internalValue, childValue];
          setInternalValue(updatedValue);
          setStringifyInternalValue(JSON.stringify(updatedValue));
        } else {
          const updatedValue = internalValue.filter(item => item !== childValue);
          setInternalValue(updatedValue);
          setStringifyInternalValue(JSON.stringify(updatedValue));
        }
      }
    }), props.children || childValue);
  }))) : null);
});
Select.displayName = "Select";
Select.propTypes = {
  /** The current validity state of the Input */
  validityState: PropTypes.oneOf(["error", "warning", "success", "unknown"]),
  /** A boolean indicating if the Select should be rendered in a smaller height */
  small: PropTypes.bool,
  /** Content to render inside the Select Component. Children should be <option> elements. */
  children: PropTypes.node.isRequired,
  /** Used to reference the input in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,
  /** Whether the input is required to be filled out to validate */
  required: PropTypes.bool,
  /** The value of the Select. If `multiple` is being used, this should be an array of values. */
  value: PropTypes.node,
  /** Callback to execute when the value changes */
  onChange: PropTypes.func,
  /** Default value for uncontrolled use. If `multiple` is being used, this should be an array of values. */
  defaultValue: PropTypes.node,
  /** Indicates that multiple options can be selected in the list */
  multiple: PropTypes.bool
};
export default Select;